// src/components/DocumentDetail.js

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from './Header';
import MobileMenu from './MobileMenu';
import MobileHeader from './MobileHeader';
import Footer from './Footer';
import FloatingMenu from './FloatingMenu';
import SEO from './SEO';
import useTeachingDocument from '../hooks/useTeachingDocument';
import { getAccessToken, getRefreshToken, removeTokens, setAccessToken } from '../api/authUtils';
import { jwtDecode } from 'jwt-decode';
import apiClient from '../api/apiClient';
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Avatar,
  Chip,
  Divider,
  Grid,
  Link,
  Tooltip,
  Paper,
  Button,
  Container,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Banner,
  useMediaQuery,
  useTheme
} from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import LanguageIcon from '@mui/icons-material/Language';
import LinkIcon from '@mui/icons-material/Link';
import TikTokIcon from '@mui/icons-material/MusicNote';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TopicIcon from '@mui/icons-material/Topic';
import SchoolIcon from '@mui/icons-material/School';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LockIcon from '@mui/icons-material/Lock';
import PreviewIcon from '@mui/icons-material/Preview';
import InfoIcon from '@mui/icons-material/Info';
import useGetProfile from '../api/profile/useGetProfile';
import './styles/DocumentDetail.css';

const DocumentDetail = () => {
  const { documentName } = useParams();
  const { document, loading, error, fetchDocumentByName } = useTeachingDocument();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFloatingMenu, setShowFloatingMenu] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const navigate = useNavigate();
  
  // Aggiungiamo il supporto per rilevare se siamo su dispositivo mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Ottieni informazioni sul profilo utente, incluso il tipo di abbonamento
  const { profile, profileLoading } = useGetProfile();
  const subscriptionType = profile?.subscription_type || 'free';
  
  // Verifica se l'utente ha un abbonamento a pagamento (mensile o annuale)
  const hasPaidSubscription = isLoggedIn && (subscriptionType === 'monthly' || subscriptionType === 'annual');
  
  // Verifica se l'utente ha un free trial
  const hasFreeTrialSubscription = isLoggedIn && subscriptionType === 'free' && hasSubscription;

  const checkAuthStatus = async () => {
    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    if (accessToken) {
      try {
        const decodedAccessToken = jwtDecode(accessToken);
        const currentTime = Date.now() / 1000;

        if (decodedAccessToken.exp && decodedAccessToken.exp > currentTime) {
          setIsLoggedIn(true);
          setHasSubscription(decodedAccessToken.has_subscription);
        } else if (refreshToken) {
          try {
            const response = await apiClient.post('/api/accounts/token/refresh/', {
              refresh: refreshToken,
            });

            if (response.status === 200) {
              const data = response.data;
              setAccessToken(data.access);
              const decodedNewAccessToken = jwtDecode(data.access);
              setIsLoggedIn(true);
              setHasSubscription(decodedNewAccessToken.has_subscription);
            } else {
              removeTokens();
              setIsLoggedIn(false);
              setHasSubscription(false);
            }
          } catch (error) {
            console.error('Errore durante il refresh del token:', error);
            removeTokens();
            setIsLoggedIn(false);
            setHasSubscription(false);
          }
        } else {
          removeTokens();
          setIsLoggedIn(false);
          setHasSubscription(false);
        }
      } catch (error) {
        console.error('Errore durante la decodifica del token di accesso:', error);
        removeTokens();
        setIsLoggedIn(false);
        setHasSubscription(false);
      }
    } else if (refreshToken) {
      try {
        const response = await apiClient.post('/api/accounts/token/refresh/', {
          refresh: refreshToken,
        });

        if (response.status === 200) {
          const data = response.data;
          setAccessToken(data.access);
          const decodedNewAccessToken = jwtDecode(data.access);
          setIsLoggedIn(true);
          setHasSubscription(decodedNewAccessToken.has_subscription);
        } else {
          removeTokens();
          setIsLoggedIn(false);
          setHasSubscription(false);
        }
      } catch (error) {
        console.error('Errore durante il refresh del token:', error);
        removeTokens();
        setIsLoggedIn(false);
        setHasSubscription(false);
      }
    } else {
      setIsLoggedIn(false);
      setHasSubscription(false);
    }
  };

  useEffect(() => {
    checkAuthStatus();
  }, [navigate]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowFloatingMenu(true);
      } else {
        setShowFloatingMenu(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    if (documentName) {
      fetchDocumentByName(documentName).catch((err) => {
        console.error('Errore nel caricamento del documento:', err);
        setSnackbar({
          open: true,
          message: 'Errore nel caricamento del documento',
          severity: 'error'
        });
      });
    }
  }, [documentName, fetchDocumentByName]);

  // Verifica se l'utente può accedere al contenuto completo
  const canAccessFullContent = () => {
    if (!document) return false;
    
    // Se è marcato come preview limitata, non possiamo accedere al contenuto completo
    if (document.is_limited_preview) return false;
    
    // Se non è premium, tutti possono accedervi
    if (!document.premium) return true;
    
    // Se è premium, solo utenti con abbonamento a pagamento possono accedervi
    return hasPaidSubscription;
  };

  // Verifica se mostrare un'anteprima limitata
  const shouldShowLimitedPreview = () => {
    if (!document) return false;
    return document.premium && (!hasPaidSubscription || document.is_limited_preview);
  };
  
  // Verifica se mostrare il messaggio per utenti con free trial
  const shouldShowFreeTrialMessage = () => {
    if (!document) return false;
    return document.premium && hasFreeTrialSubscription;
  };

  // Verifica se mostrare un messaggio per utenti non loggati
  const shouldShowNotLoggedInMessage = () => {
    if (!document) return false;
    return document.premium && !isLoggedIn;
  };

  // Formatta la data in un formato più leggibile
  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return new Date(dateString).toLocaleDateString('it-IT', options);
  };

  // Render delle etichette (tags) con tooltip
  const renderTags = (items) => {
    if (!items || items.length === 0) return null;
    
    // Limitiamo il numero di tag visibili per ogni categoria
    const maxVisibleTags = 3;
    const visibleItems = items.slice(0, maxVisibleTags);
    const hiddenItems = items.slice(maxVisibleTags);
    
    return (
      <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
        {visibleItems.map((item) => (
          <Tooltip key={item.id} title={item.nome} placement="top">
            <Chip
              label={item.nome}
              size="small"
              className="documentDetail_Tag"
            />
          </Tooltip>
        ))}
        
        {/* Mostra un chip "+X" se ci sono più tag */}
        {hiddenItems.length > 0 && (
          <Tooltip 
            title={hiddenItems.map(item => item.nome).join(', ')} 
            placement="top"
          >
            <Chip
              label={`+${hiddenItems.length}`}
              size="small"
              className="documentDetail_MoreTag"
            />
          </Tooltip>
        )}
      </Box>
    );
  };

  // Render dei social media links
  const renderSocialLinks = (doc) => {
    if (!doc) return null;

    const hasSocialLinks = doc.instagram_link || doc.facebook_link || doc.website_link || 
                           doc.generic_link || doc.eduki_link || doc.edudoro_link || 
                           doc.freeed_link || doc.tiktok_link;
    
    if (!hasSocialLinks) return null;

    return (
      <Box className="documentDetail_SocialLinks">
        <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
          Link utili:
        </Typography>
        <Grid container spacing={1} className="documentDetail_SocialGrid">
          {doc.instagram_link && (
            <Grid item>
              <Tooltip title="Instagram">
                <Link href={doc.instagram_link} target="_blank" rel="noopener noreferrer" className="documentDetail_SocialIcon">
                  <InstagramIcon />
                </Link>
              </Tooltip>
            </Grid>
          )}
          
          {doc.facebook_link && (
            <Grid item>
              <Tooltip title="Facebook">
                <Link href={doc.facebook_link} target="_blank" rel="noopener noreferrer" className="documentDetail_SocialIcon">
                  <FacebookIcon />
                </Link>
              </Tooltip>
            </Grid>
          )}
          
          {doc.website_link && (
            <Grid item>
              <Tooltip title="Sito Web">
                <Link href={doc.website_link} target="_blank" rel="noopener noreferrer" className="documentDetail_SocialIcon">
                  <LanguageIcon />
                </Link>
              </Tooltip>
            </Grid>
          )}
          
          {doc.tiktok_link && (
            <Grid item>
              <Tooltip title="TikTok">
                <Link href={doc.tiktok_link} target="_blank" rel="noopener noreferrer" className="documentDetail_SocialIcon">
                  <TikTokIcon />
                </Link>
              </Tooltip>
            </Grid>
          )}
          
          {doc.generic_link && (
            <Grid item>
              <Tooltip title="Link Generico">
                <Link href={doc.generic_link} target="_blank" rel="noopener noreferrer" className="documentDetail_SocialIcon">
                  <LinkIcon />
                </Link>
              </Tooltip>
            </Grid>
          )}
        </Grid>

        {(doc.eduki_link || doc.edudoro_link || doc.freeed_link) && (
          <Box mt={2}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Altre risorse dell'autrice su:
            </Typography>
            <Grid container spacing={1} mt={1}>
              {doc.eduki_link && (
                <Grid item>
                  <Link href={doc.eduki_link} target="_blank" rel="noopener noreferrer" className="documentDetail_PlatformLink">
                    Eduki
                  </Link>
                </Grid>
              )}
              
              {doc.edudoro_link && (
                <Grid item>
                  <Link href={doc.edudoro_link} target="_blank" rel="noopener noreferrer" className="documentDetail_PlatformLink">
                    Edudoro
                  </Link>
                </Grid>
              )}
              
              {doc.freeed_link && (
                <Grid item>
                  <Link href={doc.freeed_link} target="_blank" rel="noopener noreferrer" className="documentDetail_PlatformLink">
                    FreeEd
                  </Link>
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Box>
    );
  };

  // Render dell'autore
  const renderAuthor = (doc) => {
    if (!doc || (!doc.author_name && !doc.logo)) return null;
    
    return (
      <Box className="documentDetail_AuthorSection">
        <Box display="flex" alignItems="center" gap={2}>
          {doc.logo ? (
            <Avatar 
              src={doc.logo} 
              alt={doc.author_name || "Logo"} 
              className="documentDetail_AuthorLogo"
              sx={{ width: 48, height: 48 }}
            />
          ) : (
            doc.author_name && (
              <Avatar 
                className="documentDetail_AuthorLogo"
                sx={{ width: 48, height: 48, bgcolor: '#df4634' }}
              >
                {doc.author_name.charAt(0)}
              </Avatar>
            )
          )}
          <Box>
            <Typography variant="caption" color="textSecondary">
              Creato da:
            </Typography>
            {doc.author_name && (
              <Typography variant="body2" fontWeight="medium">
                {doc.author_name}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  // Funzione per visualizzare il PDF o l'immagine di anteprima in base al dispositivo
  const renderPDF = (doc) => {
    if (!doc || !doc.file) return null;
    
    // Se siamo su mobile, mostriamo solo l'immagine di anteprima
    if (isMobile) {
      return (
        <Box className="documentDetail_MobilePreview">
          {doc.thumbnail ? (
            <img
              src={doc.thumbnail}
              alt={`Anteprima di ${doc.document_name}`}
              className="documentDetail_MobilePreviewImage"
            />
          ) : (
            <Box className="documentDetail_PreviewPlaceholder">
              <Typography variant="body2" color="textSecondary">
                Anteprima non disponibile. 
                <a href={doc.file} target="_blank" rel="noopener noreferrer">
                  Clicca qui per scaricare il PDF
                </a>
              </Typography>
            </Box>
          )}
        </Box>
      );
    }
    
    // Altrimenti, mostriamo il PDF come prima
    return (
      <Box className="documentDetail_IframeContainer">
        <object
          data={doc.file}
          type="application/pdf"
          className="documentDetail_Iframe"
        >
          <embed
            src={doc.file}
            type="application/pdf"
            className="documentDetail_Iframe"
          />
          <p>
            Il tuo browser non può visualizzare i PDF. 
            <a href={doc.file} target="_blank" rel="noopener noreferrer">
              Clicca qui per scaricare il PDF
            </a>.
          </p>
        </object>
      </Box>
    );
  };

  // Funzione per visualizzare una preview limitata del PDF
  const renderLimitedPreview = (doc) => {
    if (!doc) return (
      <Box className="documentDetail_LimitedPreview">
        <Box className="documentDetail_PreviewPlaceholder">
          <LockIcon sx={{ fontSize: 50, color: '#df4634', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            🔒 Contenuto Premium
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ mb: 2 }}>
            Accedi a un mondo di risorse esclusive per insegnanti.
            Per visualizzare questo documento, attiva Minerva Premium.
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ mb: 2, fontWeight: 'bold' }}>
            Con soli 9,99€ al mese avrai tutte le attività didattiche di cui hai bisogno! Pagamento anche con Carta del Docente.
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ mb: 2, fontWeight: 'bold' }}>
            👉 Abbonati ora e trasforma la tua didattica!
          </Typography>
        </Box>
      </Box>
    );
    
    // Se abbiamo una thumbnail, mostriamo quella
    if (doc.thumbnail) {
      return (
        <Box className="documentDetail_LimitedPreview">
          <img 
            src={doc.thumbnail} 
            alt={`Anteprima di ${doc.document_name}`}
            className="documentDetail_PreviewImage"
          />
          <Box className="documentDetail_PreviewOverlay">
            <Box className="documentDetail_PreviewContent">
              <LockIcon sx={{ fontSize: 50, color: 'white', mb: 2 }} />
              <Typography variant="h6" color="white" gutterBottom>
                Contenuto Premium
              </Typography>
              <Typography variant="body2" color="white" textAlign="center" sx={{ mb: 2 }}>
                Accedi a un mondo di risorse esclusive per insegnanti.
                Per visualizzare questo documento, attiva Minerva Premium: avrai accesso a tantissime risorse didattiche pronte all'uso, con nuovi materiali ogni giorno, e potrai utilizzare la prima intelligenza artificiale italiana creata su misura per le insegnanti.
              </Typography>
              <Typography variant="body2" color="white" textAlign="center" sx={{ mb: 2, fontWeight: 'bold' }}>
                Con soli 9,99€ al mese avrai tutte le attività didattiche di cui hai bisogno! Pagamento anche con Carta del Docente.
              </Typography>
              <Typography variant="body2" color="white" textAlign="center" sx={{ mb: 2, fontWeight: 'bold' }}>
                👉 Abbonati ora e trasforma la tua didattica!
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    }
    
    // Se non abbiamo una thumbnail, mostriamo un messaggio generico
    return (
      <Box className="documentDetail_LimitedPreview">
        <Box className="documentDetail_PreviewPlaceholder">
          <LockIcon sx={{ fontSize: 50, color: '#df4634', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            🔒 Contenuto Premium
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ mb: 2 }}>
            Accedi a un mondo di risorse esclusive per insegnanti.
            Per visualizzare questo documento, attiva Minerva Premium: avrai accesso a oltre 50 risorse didattiche pronte all'uso, con nuovi materiali ogni giorno, e potrai utilizzare la prima intelligenza artificiale italiana creata su misura per le insegnanti.
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ mb: 2, fontWeight: 'bold' }}>
            Con soli 9,99€ al mese avrai tutte le attività didattiche di cui hai bisogno! Pagamento anche con Carta del Docente.
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center" sx={{ mb: 2, fontWeight: 'bold' }}>
            👉 Abbonati ora e trasforma la tua didattica!
          </Typography>
        </Box>
      </Box>
    );
  };

  // Funzione per renderizzare il messaggio per utenti con free trial
  const renderFreeTrialMessage = () => {
    return (
      <Box 
        className="documentDetail_FreeTrialAlert" 
        sx={{ 
          mb: 3, 
          mt: document?.premium ? 2 : 0, 
          p: 2, 
          borderRadius: 1,
          backgroundColor: 'rgba(245, 215, 110, 0.15)',
          borderLeft: '4px solid #f5d76e',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <InfoIcon sx={{ color: '#f5d76e', mr: 2, fontSize: 24 }} />
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#5d4037' }}>
            Contenuto Premium richiede abbonamento a pagamento
          </Typography>
          <Typography variant="body2" sx={{ color: '#5d4037' }}>
            Questo documento è accessibile solo con abbonamento mensile o annuale, non con il periodo di prova gratuito.
          </Typography>
        </Box>
      </Box>
    );
  };

  // Funzione per renderizzare il messaggio per utenti non loggati
  const renderNotLoggedInMessage = () => {
    return (
      <Box 
        className="documentDetail_NotLoggedInAlert" 
        sx={{ 
          mb: 3, 
          mt: document?.premium ? 2 : 0, 
          p: 2, 
          borderRadius: 1,
          backgroundColor: 'rgba(223, 70, 52, 0.08)',
          borderLeft: '4px solid #df4634',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <InfoIcon sx={{ color: '#df4634', mr: 2, fontSize: 24 }} />
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#5d4037' }}>
            🔒 Contenuto Premium
          </Typography>
          <Typography variant="body2" sx={{ color: '#5d4037' }}>
            Accedi a un mondo di risorse esclusive per insegnanti.
            Per visualizzare questo documento, attiva Minerva Premium: avrai accesso a oltre 50 risorse didattiche pronte all'uso, con nuovi materiali ogni giorno, e potrai utilizzare la prima intelligenza artificiale italiana creata su misura per le insegnanti.
          </Typography>
          <Typography variant="body2" sx={{ color: '#5d4037', mt: 1, fontWeight: 500 }}>
            Con soli 9,99€ al mese avrai tutte le attività didattiche di cui hai bisogno! Pagamento anche con Carta del Docente.
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box className="documentDetail_MainContainer">
      {document && (
        <SEO 
          title={`${document.document_name} - Attività Didattica Minerva`}
          description={document.description || `Scopri l'attività didattica "${document.document_name}" su Minerva. ${document.subtitle || 'Risorsa educativa pronta all\'uso per insegnanti.'}`}
          keywords={`attività didattica, ${document.document_name}, ${document.argomenti?.map(a => a.nome).join(', ') || ''}, ${document.anni_scolastici?.map(a => a.nome).join(', ') || ''}, risorse didattiche, minerva education`}
          ogType="article"
          ogUrl={`https://minervaeducationai.it/documents/detail/${encodeURIComponent(document.document_name)}`}
          canonical={`https://minervaeducationai.it/documents/detail/${encodeURIComponent(document.document_name)}`}
        />
      )}
      
      <Header
        isLoggedIn={isLoggedIn}
        setIsLoggedIn={setIsLoggedIn}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      {/* Aggiungiamo il MobileHeader */}
      <MobileHeader 
        isLoggedIn={isLoggedIn} 
        setMobileMenuOpen={setMobileMenuOpen} 
      />

      {mobileMenuOpen && (
        <MobileMenu isLoggedIn={isLoggedIn} setMobileMenuOpen={setMobileMenuOpen} />
      )}

      {showFloatingMenu && (
        <FloatingMenu
          isLoggedIn={isLoggedIn}
          handleLogout={() => {
            removeTokens();
            setIsLoggedIn(false);
            setHasSubscription(false);
          }}
        />
      )}

      <Container maxWidth="lg">
        <main className="documentDetail_MainContent">
          {loading || profileLoading ? (
            <Box className="documentDetail_LoadingBox">
              <CircularProgress sx={{ color: '#df4634' }} />
            </Box>
          ) : error ? (
            <Typography color="error">Errore nel caricamento del documento: {error.message}</Typography>
          ) : document ? (
            <>
              <Typography variant="h4" component="h1" className="documentDetail_MainTitle" gutterBottom>
                {document.document_name}
                {document.premium && (
                  <Chip 
                    label="Premium" 
                    size="small" 
                    className="documentDetail_PremiumChip"
                    sx={{ ml: 2, verticalAlign: 'middle' }}
                  />
                )}
              </Typography>
              
              {document.subtitle && (
                <Typography variant="h6" color="textSecondary" gutterBottom className="documentDetail_MainSubtitle">
                  {document.subtitle}
                </Typography>
              )}
              
              {/* Mostra messaggio per utenti con free trial se documento è premium */}
              {shouldShowFreeTrialMessage() && renderFreeTrialMessage()}
              
              {/* Mostra messaggio per utenti non loggati se documento è premium */}
              {shouldShowNotLoggedInMessage() && renderNotLoggedInMessage()}

              <Grid container spacing={3} className="documentDetail_ContentGrid">
                {/* Colonna sinistra con PDF o preview limitata */}
                <Grid item xs={12} md={8}>
                  <Box className="documentDetail_PreviewContainer" sx={{ boxShadow: 'none', border: 'none' }}>
                    {canAccessFullContent() ? (
                      // Contenuto completo per utenti con abbonamento a pagamento o contenuti non premium
                      <>
                        {renderPDF(document)}
                        <Box className="documentDetail_PreviewActions" sx={{ background: 'white', borderTop: 'none' }}>
                          <Button
                            variant="contained"
                            startIcon={<CloudDownloadIcon />}
                            className="documentDetail_DownloadBtn"
                            component="a" 
                            href={document.file}
                            download={`${document.document_name}.pdf`}
                            fullWidth
                            onClick={(e) => {
                              e.preventDefault();
                              window.open(document.file, '_blank');
                            }}
                          >
                            Scarica PDF
                          </Button>
                        </Box>
                      </>
                    ) : (
                      // Contenuto limitato per utenti non loggati, con free trial o senza abbonamento
                      <>
                        {renderLimitedPreview(document)}
                        <Box className="documentDetail_PreviewActions" sx={{ background: 'white', borderTop: 'none' }}>
                          <Button
                            variant="contained"
                            startIcon={<PreviewIcon />}
                            className="documentDetail_SubscribeBtn"
                            onClick={() => isLoggedIn ? navigate('/abbonamento') : navigate('/welcomepage')}
                            fullWidth
                            sx={{ 
                              background: 'linear-gradient(45deg, #df4634 30%, #f76f62 90%)',
                              boxShadow: '0 3px 5px 2px rgba(223, 70, 52, .3)'
                            }}
                          >
                            {!isLoggedIn 
                              ? 'Accedi per sbloccare' 
                              : hasFreeTrialSubscription 
                                ? 'Passa a un abbonamento a pagamento' 
                                : 'Abbonati per accedere'}
                          </Button>
                        </Box>
                      </>
                    )}
                    
                    {/* Link utili spostati qui sotto il PDF */}
                    {renderSocialLinks(document)}
                  </Box>
                </Grid>
                
                {/* Colonna destra con informazioni e descrizione */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      {!document.premium && (
                        <Chip 
                          label="Nuova pubblicazione" 
                          size="small" 
                          className="documentDetail_NewChip"
                        />
                      )}
                      
                      <Chip
                        icon={<CalendarTodayIcon sx={{ fontSize: '0.8rem !important' }} />}
                        label={formatDate(document.uploaded_at)}
                        size="small"
                        variant="outlined"
                        className="documentDetail_DateChip"
                      />
                    </Box>

                    {renderAuthor(document)}
                    
                    {/* Descrizione - sempre visibile a tutti */}
                    {document.description && (
                      <Box className="documentDetail_InfoSection" mt={3}>
                        <Typography variant="subtitle2" fontWeight="medium" mb={1}>
                          Descrizione:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          className="documentDetail_Description"
                          sx={{ overflowWrap: 'break-word' }}
                        >
                          {document.description}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Informazioni sul documento sempre visibili a tutti */}
                    <Box className="documentDetail_InfoPanel" sx={{ boxShadow: 'none', border: 'none', mt: 3 }}>
                      <Box className="documentDetail_CompactInfo" sx={{ background: 'white' }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                          Informazioni sul documento
                        </Typography>
                        
                        <Grid container spacing={2}>
                          {/* Argomenti */}
                          {document.argomenti && document.argomenti.length > 0 && (
                            <Grid item xs={12}>
                              <Box className="documentDetail_InfoItem">
                                <Typography variant="body2" className="documentDetail_InfoLabel">
                                  <TopicIcon sx={{ fontSize: 16, mr: 1 }} />
                                  Argomenti
                                </Typography>
                                <Box className="documentDetail_InfoValue">
                                  {renderTags(document.argomenti)}
                                </Box>
                              </Box>
                            </Grid>
                          )}
                          
                          {/* Anni scolastici e Tipi di materiale in layout orizzontale */}
                          <Grid item xs={12} container spacing={2}>
                            {/* Anni scolastici */}
                            {document.anni_scolastici && document.anni_scolastici.length > 0 && (
                              <Grid item xs={12} md={6}>
                                <Box className="documentDetail_InfoItem">
                                  <Typography variant="body2" className="documentDetail_InfoLabel">
                                    <SchoolIcon sx={{ fontSize: 16, mr: 1 }} />
                                    Anni
                                  </Typography>
                                  <Box className="documentDetail_InfoValue">
                                    {renderTags(document.anni_scolastici)}
                                  </Box>
                                </Box>
                              </Grid>
                            )}
                            
                            {/* Tipi di materiale */}
                            {document.tipi_materiale && document.tipi_materiale.length > 0 && (
                              <Grid item xs={12} md={6}>
                                <Box className="documentDetail_InfoItem">
                                  <Typography variant="body2" className="documentDetail_InfoLabel">
                                    <MenuBookIcon sx={{ fontSize: 16, mr: 1 }} />
                                    Tipi
                                  </Typography>
                                  <Box className="documentDetail_InfoValue">
                                    {renderTags(document.tipi_materiale)}
                                  </Box>
                                </Box>
                              </Grid>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    </Box>
                    
                    {/* Call to action per abbonamento */}
                    {shouldShowLimitedPreview() && (
                      <Card sx={{ mt: 3, borderRadius: '8px', overflow: 'hidden', border: '1px solid #eaeaea' }}>
                        <CardContent sx={{ p: 2 }}>
                          <Typography variant="h6" fontWeight="bold" gutterBottom>
                            🔐 Contenuto Premium
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Questo documento fa parte delle risorse riservate agli utenti Minerva Premium.
                            Con l'abbonamento sblocchi:
                          </Typography>
                          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                            <Typography component="li" variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                              Oltre 50 attività, schede e laboratori pronti, con nuove risorse ogni giorno
                            </Typography>
                            <Typography component="li" variant="body2" color="textSecondary">
                              Accesso completo all'AI educativa n°1 in Italia per insegnanti della scuola primaria
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="textSecondary" sx={{ mb: 1, fontWeight: 'bold' }}>
                            ✨ Non perdere l'occasione di semplificarti la vita in classe.
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            Abbonati a Minerva Premium per accedere subito.
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontWeight: 'bold', color: '#df4634' }}>
                            Con soli 9,99€ al mese avrai tutte le attività didattiche di cui hai bisogno! Pagamento anche con Carta del Docente.
                          </Typography>
                        </CardContent>
                        <CardActions sx={{ p: 2, pt: 0 }}>
                          <Button 
                            variant="contained" 
                            fullWidth
                            onClick={() => isLoggedIn ? navigate('/abbonamento') : navigate('/welcomepage')}
                            sx={{ 
                              background: 'linear-gradient(45deg, #df4634 30%, #f76f62 90%)',
                              boxShadow: '0 3px 5px 2px rgba(223, 70, 52, .3)'
                            }}
                          >
                            {!isLoggedIn 
                              ? 'Abbonati ora e trasforma la tua didattica!' 
                              : hasFreeTrialSubscription 
                                ? 'Passa a un abbonamento a pagamento' 
                                : 'Scopri i piani di abbonamento'}
                          </Button>
                        </CardActions>
                      </Card>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </>
          ) : (
            <Typography>Nessun documento selezionato.</Typography>
          )}
        </main>
      </Container>

      <Footer />

      {/* Snackbar per le notifiche */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentDetail;